import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

const logEventSilent = async (params: Record<string, unknown>) => {
  try { await supabase.functions.invoke('log-event', { body: params }); } catch (_) { /* silent */ }
};

type AppRole = 'superadmin' | 'admin' | 'gestor' | 'lider' | 'funcionario';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  role: AppRole | null;
  empresaId: string | null;
  loading: boolean;
  mfaRequired: boolean;
  refreshMfa: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  // Impersonation
  impersonatedEmpresaId: string | null;
  impersonatedEmpresaNome: string | null;
  isImpersonating: boolean;
  enterEmpresa: (empresaId: string, nome: string) => Promise<void>;
  exitEmpresa: () => Promise<void>;
  // Garante que a sessão de empresa existe no servidor antes de gravar dados
  ensureEmpresaContext: () => Promise<boolean>;
}

const IMPERSONATION_KEY = 'impersonated_empresa';


const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [role, setRole] = useState<AppRole | null>(null);
  const [realEmpresaId, setRealEmpresaId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [mfaRequired, setMfaRequired] = useState(false);

  // True when the account has a verified 2FA factor but the current session
  // has not completed the TOTP challenge yet (aal1 while aal2 is required).
  const checkMfa = async () => {
    try {
      const { data } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      const pending = !!data && data.nextLevel === 'aal2' && data.nextLevel !== data.currentLevel;
      setMfaRequired(pending);
      return pending;
    } catch (_) {
      setMfaRequired(false);
      return false;
    }
  };

  const refreshMfa = async () => { await checkMfa(); };

  // Impersonation state (persistida localmente para sobreviver a reloads/abas)
  const readStored = (): { id: string; nome: string } | null => {
    try {
      const raw = localStorage.getItem(IMPERSONATION_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (_) { return null; }
  };
  const [impersonatedEmpresaId, setImpersonatedEmpresaId] = useState<string | null>(() => readStored()?.id ?? null);
  const [impersonatedEmpresaNome, setImpersonatedEmpresaNome] = useState<string | null>(() => readStored()?.nome ?? null);

  const storeImpersonation = (id: string | null, nome: string | null) => {
    try {
      if (id) localStorage.setItem(IMPERSONATION_KEY, JSON.stringify({ id, nome }));
      else localStorage.removeItem(IMPERSONATION_KEY);
    } catch (_) { /* ignore */ }
    setImpersonatedEmpresaId(id);
    setImpersonatedEmpresaNome(nome);
  };

  const isImpersonating = role === 'superadmin' && impersonatedEmpresaId !== null;

  // When superadmin is impersonating, empresaId returns the impersonated one
  const empresaId = isImpersonating ? impersonatedEmpresaId : realEmpresaId;

  const upsertSession = async (userId: string, id: string) => {
    const { error } = await supabase.from('impersonation_sessions').upsert(
      { user_id: userId, empresa_id: id, started_at: new Date().toISOString(), ended_at: null },
      { onConflict: 'user_id' }
    );
    return !error;
  };

  const enterEmpresa = async (id: string, nome: string) => {
    if (!user) return;
    await upsertSession(user.id, id);
    storeImpersonation(id, nome);
  };

  const exitEmpresa = async () => {
    if (!user) return;
    await supabase.from('impersonation_sessions').delete().eq('user_id', user.id);
    storeImpersonation(null, null);
  };

  // Confirma (e recria, se preciso) a linha de impersonação no servidor antes de gravar
  const ensureEmpresaContext = async (): Promise<boolean> => {
    if (!user || role !== 'superadmin' || !impersonatedEmpresaId) return true;
    const { data } = await supabase
      .from('impersonation_sessions')
      .select('empresa_id')
      .eq('user_id', user.id)
      .is('ended_at', null)
      .maybeSingle();
    if (data?.empresa_id === impersonatedEmpresaId) return true;
    return await upsertSession(user.id, impersonatedEmpresaId);
  };

  const fetchRoleAndEmpresa = async (userId: string) => {
    const [{ data: roleData }, { data: profileData }, { data: sessionData }] = await Promise.all([
      supabase.from('user_roles').select('role').eq('user_id', userId).limit(1).maybeSingle(),
      supabase.from('profiles').select('empresa_id').eq('user_id', userId).limit(1).maybeSingle(),
      supabase.from('impersonation_sessions').select('empresa_id').eq('user_id', userId).is('ended_at', null).limit(1).maybeSingle(),
    ]);
    const fetchedRole = (roleData?.role as AppRole) ?? null;
    if (fetchedRole === 'superadmin') {
      const stored = readStored();
      const targetId = sessionData?.empresa_id ?? stored?.id ?? null;
      if (targetId) {
        // Servidor sem sessão ativa mas com empresa guardada localmente: recriar
        if (!sessionData?.empresa_id) await upsertSession(userId, targetId);
        const { data: empresaData } = await supabase.from('empresas').select('nome').eq('id', targetId).maybeSingle();
        storeImpersonation(targetId, empresaData?.nome ?? stored?.nome ?? 'Empresa');
      }
    } else {
      storeImpersonation(null, null);
    }
    return {
      role: fetchedRole,
      empresaId: (profileData as any)?.empresa_id ?? null,
    };
  };

  const ensureEmpresaContextRef = useRef<(() => Promise<boolean>) | null>(null);
  ensureEmpresaContextRef.current = ensureEmpresaContext;




  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user) {
          setTimeout(async () => {
            await checkMfa();
            const { role, empresaId } = await fetchRoleAndEmpresa(session.user.id);
            setRole(role);
            setRealEmpresaId(empresaId);
            setLoading(false);
          }, 0);
        } else {
          setRole(null);
          setRealEmpresaId(null);
          setMfaRequired(false);
          storeImpersonation(null, null);
          setLoading(false);
        }

      }
    );

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        await checkMfa();
        const { role, empresaId } = await fetchRoleAndEmpresa(session.user.id);
        setRole(role);
        setRealEmpresaId(empresaId);
      }
      setLoading(false);
    });

    // Revalida a sessão de empresa ao voltar o foco ao separador
    const onVisible = () => { if (document.visibilityState === 'visible') { void ensureEmpresaContextRef.current?.(); } };
    document.addEventListener('visibilitychange', onVisible);

    return () => { subscription.unsubscribe(); document.removeEventListener('visibilitychange', onVisible); };

  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (!error) {
      logEventSilent({ nivel: 'info', categoria: 'autenticacao', acao: 'LOGIN', user_email: email, sucesso: true });
    }
    return { error: error as Error | null };
  };

  const signOut = async () => {
    logEventSilent({ nivel: 'info', categoria: 'autenticacao', acao: 'LOGOUT', user_email: user?.email, sucesso: true });
    if (user) {
      await supabase.from('impersonation_sessions').delete().eq('user_id', user.id);
    }
    storeImpersonation(null, null);
    setMfaRequired(false);
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{
      user, session, role, empresaId, loading, mfaRequired, refreshMfa, signIn, signOut,
      impersonatedEmpresaId, impersonatedEmpresaNome, isImpersonating,
      enterEmpresa, exitEmpresa, ensureEmpresaContext,
    }}>

      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
